import { Suspense, useState } from 'react';
import { enableViewFavoriting } from '@gonfalon/dogfood-flags/dogfood-flags';
import { pluralize } from '@gonfalon/format/pluralize';
import { Box } from '@launchpad-ui/box';
import { Button, MenuTrigger, Popover, ProgressBar, Tag, TagGroup, TagList } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { View } from '../types';
import { ViewsDialog } from '../ViewsDialog';

import styles from './ViewsSelect.module.css';

type ViewsSelectPopoverMaxHeight = number | string;

export type ViewsSelectProps = {
  onSelectionChange: (views: View[]) => void;
  selectedViews: View[];
  label?: string;
  showSelectionTags?: boolean;
  className?: string;
  popoverMaxHeight?: ViewsSelectPopoverMaxHeight;
  selectionMode?: 'single' | 'multiple';
  hideCreateButton?: boolean;
  isDisabled?: boolean;
  /**
   * Project key to scope the views query against. Required when this picker is mounted outside
   * the `project-context` route loader (e.g. payload-filtering-v2's `NewSdkKeyModal` at
   * `/settings/sdk-keys`). When omitted, the underlying `ViewsDialog` falls back to the active
   * project context.
   */
  projectKey?: string;
};

export function ViewsSelect({
  onSelectionChange,
  selectedViews = [],
  label = 'Select views',
  showSelectionTags = false,
  className,
  popoverMaxHeight = 300,
  selectionMode,
  hideCreateButton,
  isDisabled,
  projectKey,
}: ViewsSelectProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isCompactRichDialogEnabled = enableViewFavoriting();

  // Reset internal open state synchronously during render when the trigger is
  // disabled. React Aria's `MenuTrigger` does not fire `onOpenChange` for
  // controlled prop changes, so without this the stale `isPopoverOpen = true`
  // would resurface the moment `isDisabled` flips back to `false` and the
  // popover would reopen without user interaction. Computing the reset during
  // render (instead of via `useEffect`) lets React discard this render and
  // re-render synchronously before paint — no wasted commit.
  if (isDisabled && isPopoverOpen) {
    setIsPopoverOpen(false);
  }
  const popoverMaxHeightValue = typeof popoverMaxHeight === 'number' ? `${popoverMaxHeight}px` : popoverMaxHeight;
  const popoverContentStyle = isCompactRichDialogEnabled
    ? ({ maxHeight: popoverMaxHeightValue, padding: 0 } as const)
    : ({
        maxHeight: popoverMaxHeightValue,
        overflowY: 'auto',
        padding: 0,
      } as const);

  const getLabelText = () => {
    if (selectedViews.length === 0) {
      return label;
    }

    return `${selectedViews.length} ${pluralize(selectedViews.length, 'view', 'views')}`;
  };

  // When disabled, avoid rendering/opening the popover entirely — forward
  // isDisabled to the trigger button and keep the menu closed.
  const handleOpenChange = (nextOpen: boolean) => {
    if (isDisabled) {
      setIsPopoverOpen(false);
      return;
    }
    setIsPopoverOpen(nextOpen);
  };

  return (
    <Box
      display="flex"
      gap={showSelectionTags && selectedViews.length > 0 ? '$400' : '$0'}
      flexDirection="column"
      alignItems="flex-start"
    >
      <MenuTrigger isOpen={isDisabled ? false : isPopoverOpen} onOpenChange={handleOpenChange}>
        <Button className={className} isDisabled={isDisabled}>
          {getLabelText()} <Icon name="chevron-down" size="small" />
        </Button>
        <Popover
          maxHeight={typeof popoverMaxHeight === 'number' ? popoverMaxHeight : undefined}
          style={isCompactRichDialogEnabled ? { padding: 0 } : undefined}
          className={isCompactRichDialogEnabled ? styles.compactPopover : undefined}
        >
          <div
            style={popoverContentStyle}
            className={isCompactRichDialogEnabled ? styles.compactPopoverContent : undefined}
          >
            <Suspense
              fallback={
                <Box width="$320" height="$208" display="grid" alignItems="center" justifyContent="center">
                  <ProgressBar aria-label="Loading…" isIndeterminate size="small" />
                </Box>
              }
            >
              <ViewsDialog
                label={label}
                selectedViews={selectedViews}
                onSelectionChange={onSelectionChange}
                onBeforeCreateView={() => setIsPopoverOpen(false)}
                selectionMode={selectionMode}
                hideCreateButton={hideCreateButton}
                projectKey={projectKey}
              />
            </Suspense>
          </div>
        </Popover>
      </MenuTrigger>
      {showSelectionTags && (
        <TagGroup aria-label={`Selected ${pluralize(selectedViews.length, 'view', 'views')}`}>
          <TagList items={selectedViews}>
            {(item) => (
              <Tag key={item.key} textValue={item.name} size="small">
                {item.name}
              </Tag>
            )}
          </TagList>
        </TagGroup>
      )}
    </Box>
  );
}
